

import { FieldType, FormFieldConfig } from "Components/Common/CommonForm";
import { MenuItem, Study, StudyFilterFormValues, StudyFormValues, StudySection, TabConfig } from "./study.types";
import { getLabelFromValue } from "helpers/constant";
import { Col, Row } from "reactstrap";
import { validations } from "validations/FormValidations";
import { Column } from "Components/Common/CommonTableIndexWise";
import viewStyles from "../../../../assets/scss/common/viewDetails.module.scss";
import { format, parseISO, isValid } from "date-fns";
import { MASTER_STUDY_PATHS } from "Routes/paths";
import { AMBULATORY_VISIT_CATEGORY_OPTIONS, AMBULATORY_VISIT_TYPE_OPTIONS, Biological_matrix_OPTIONS, Fasting_Condition_OPTIONS, Gender_OPTIONS, Light_Condition_OPTIONS, Routeof_Administration_OPTIONS, SPONSOR_NAME_OPTIONS, StudyDesign_OPTIONS, StudyType_OPTIONS } from "api/modules/common.option";
import CommonFlatpickr from "Components/Common/CommonDatePicker";
import { formatDate, parseLocalDate } from "common/service/CommonService";
import TruncatedText from "Components/Common/TruncatedText";

export const studyFilterFields: FormFieldConfig[] = [
  {
    name: "studytitle",
    label: "Study Title",
    placeholder: "Enter study title",
  },
  {
    name: "studycode",
    label: "Study Code",
    placeholder: "Enter study code",
  },
  {
    name: "sponsorname",
    label: "Sponsor Name",
    type: "select",
    placeholder: "Company funding the trial",
    options: SPONSOR_NAME_OPTIONS,
  },
  {
    name: "studydesign",
    label: "Study Design",
    type: "select",
    placeholder: "Select study design",
    options: StudyDesign_OPTIONS,
  },
  {
    name: "totalsubjects",
    label: "Total Subjects",
    type: "number",
    placeholder: "Enter total subjects",
  },
  {
    name: "numberofperiods",
    label: "Number of Periods",
    type: "text",
    placeholder: "Enter number of periods",
  },
];


export const filterSection: { title: string; fields: { name: keyof StudyFilterFormValues; label: string; type: string; placeholder: string; options?: any[] }[] }[] = [
  {
    title: "STUDY INFORMATION",
    fields: [
      { name: "studytitle", label: "STUDY TITLE", type: "text", placeholder: "Enter study title" },
      { name: "studycode", label: "STUDY CODE", type: "text", placeholder: "Enter study code" },
      { name: "sponsorname", label: "SPONSOR NAME", type: "select", placeholder: "Select Sponsor", options: SPONSOR_NAME_OPTIONS },
      { name: "studydesign", label: "STUDY DESIGN", type: "select", placeholder: "Select study design", options: StudyDesign_OPTIONS },
    ],
  },
  {
    title: "SUBJECTS & PERIODS",
    fields: [
      { name: "totalsubjects", label: "TOTAL SUBJECTS", type: "number", placeholder: "Enter total subjects" },
      { name: "numberofperiods", label: "NUMBER OF PERIODS", type: "text", placeholder: "Enter number of periods" },
    ],
  },
];


//-------------------------------------------------------------------------------------------
//--> Menu Items
//-------------------------------------------------------------------------------------------

export const menuItems: MenuItem[] = [
  { key: "details", label: "Study Details", icon: "ri-file-list-3-line" },
  { key: "geography", label: "Geography & Scope", icon: "ri-map-pin-line" },
  { key: "timeline", label: "Timelines / Milestones", icon: "ri-time-line" },
];

//-------------------------------------------------------------------------------------------
//--> BreadCrum Item(s)
//-------------------------------------------------------------------------------------------

export const breadcrumbItems = [
  { text: "Study", link: "/study" },
  { text: "Study Deatils" }
]

export const truncateWithTooltip = (value: any, max = 40) => {
  if (!value || typeof value !== "string") return value;
  const isLong = value.length > max;
  const display = isLong ? value.slice(0, max) + "..." : value;
  return <span title={isLong ? value : ""}>{display}</span>;
};

export const renderStudyValue = (key: keyof StudyFormValues, value: any) => {
  // Date fields
  const dateFields = [
    "protocolversiondate",
    "ethicsapprovaldate",
    "screeningdate",
    "startdate",
    "enddate",
    "refmanufacturingdate",
    "refexpirydate",
    "testmanufacturingdate",
    "testexpirydate"
  ];

  if (dateFields.includes(key as string)) {
    if (!value) return <span className="text-muted">—</span>;
    const currentdate = formatDate(value);
    return currentdate;
  }
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted">—</span>;
  }
  if (key === "studydesign") {
    return getLabelFromValue(StudyDesign_OPTIONS, value);
  } else if (key === "gender") {
    return getLabelFromValue(Gender_OPTIONS, value);
  } else if (key === "routeofadministration") {
    return getLabelFromValue(Routeof_Administration_OPTIONS, value);
  } else if (key === "biologicalmatrix") {
    return getLabelFromValue(Biological_matrix_OPTIONS, value);
  } else if (key === "fastingcondition") {
    return getLabelFromValue(Fasting_Condition_OPTIONS, value);
  }
  else if (key === "studytype") {
    return getLabelFromValue(StudyType_OPTIONS, value);
  }
  else if (key === "lightcondition") {
    return getLabelFromValue(Light_Condition_OPTIONS, value);
  }
  else if (key === "samplingpoints") {
    return Array.isArray(value) ? value.join(", ") : value;
  }
  else if (key === "periods") {
    if (!Array.isArray(value) || value.length === 0) {
      return <span className="text-muted">—</span>;
    }

    return (
      <Row>
        {value.map((p: any, i: number) => (
          <Col lg="3" key={i}>
            <div className={viewStyles.reviewItem}>
              <div className={viewStyles.reviewLabel}>Period {p.period_no}</div>
              <div className={viewStyles.reviewValue}>
                {p.start_date
                  ? new Date(p.start_date).toLocaleDateString("en-GB")
                  : "—"}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    );
  }

  return value;
};



export const studyListColumn = (navigate: any) => [
  {
    key: "studycode",
    label: "Study Code",
    type: "link",
    tooltip: true,
    maxLength: 25,
    onClick: (row: any) => {
      sessionStorage.setItem("activestudyid", String(row.studyid));
      const isCompleted =
        String(row.isdraft || "").toLowerCase() === "completed" ||
        String(row.studystatus || "").toLowerCase() === "completed" ||
        String(row.studyStatus || "").toLowerCase() === "completed";

      if (isCompleted) {
        navigate(`/master/study/history-details/${row.studyid}`);
      } else {
        navigate(MASTER_STUDY_PATHS.DETAILS);
      }
    },
  },
  { key: "studytitle", label: "Study Title", tooltip: true, maxLength: 40 },
  { key: "sponsorname", label: "Sponsor", tooltip: true, maxLength: 40 },
  {
    key: "studydesigntext",
    label: "Design"
  },
  { key: "totalsubjects", label: "Subjects" }
];


//--------------------------------------------------------------------------------------
//--> Add study details fields
//--------------------------------------------------------------------------------------
export const fields: FormFieldConfig[] = [
  // 1. Study Identification
  {
    name: "studycode",
    label: "Study Code / ID",
    required: true,
    placeholder: "Enter study code",
    rules: validations.studyDetails.studycode,
  },
  {
    name: "studytitle",
    label: "Study Title",
    placeholder: "Enter study title",
    rules: validations.studyDetails.studytitle,
  },
  {
    name: "versionnumber",
    label: "Version Number",
    placeholder: "Enter version number",
    rules: validations.studyDetails.versionnumber,
  },
  {
    name: "protocolversiondate",
    label: "Protocol Version Date",
    type: "flatpickr",
  },

  {
    name: "sponsorname",
    label: "Sponsor Name",
    type: "select",
    options: SPONSOR_NAME_OPTIONS,
    required: true,

    // While changing sponsor Name - setting other values in that tab

    onChangeExtra: (
      selected: any,
      { setValue }: { setValue?: any },
    ) => {
      const sponsor = selected?.data;
      if (!sponsor) {
        // On clearing options : empty fields
        setValue("croname", "");
        return;
      }
      setValue("croname", sponsor.croname);
      setValue("sponsorid", sponsor.sponsorid);
      setValue("sponsorname", sponsor.sponsorname);
    },
  },
  {
    name: "croname",
    label: "CRO Name",
    placeholder: "Enter CRO name",
    disabled: true,
  },

  // 3. Regulatory & Ethics
  {
    name: "ethicscommitteename",
    label: "Ethics Committee Name",
    placeholder: "Enter ethics committee name",
    rules: validations.studyDetails.ethicscommitteename,
  },
  {
    name: "ethicsapprovaldate",
    label: "Ethics Approval Date",
    type: "flatpickr",
  },
  {
    name: "regulatoryauthority",
    label: "Regulatory Authority",
    placeholder: "Enter regulatory authority",
    rules: validations.studyDetails.regulatoryauthority,
  },
  // 4. Study Design
  {
    name: "studytype",
    label: "Study Type",
    type: "select",
    options: StudyType_OPTIONS,
    required: true,
    rules: { required: "Study Type is required" },
  },
  {
    name: "studydesign",
    label: "Study Design",
    placeholder: "Select study design",
    type: "select",
    options: StudyDesign_OPTIONS,
    required: true,
    onChangeExtra: (selected: any, { setValue }: { setValue?: any }) => {
      const design = selected?.data;
      if (!design) {
        setValue("studydesigndata", null);
        setValue("numberofsequences", "");
        setValue("numberofperiods", "");
        setValue("numberoftreatments", "");
        return;
      }

      const sequenceType = design.sequencetype || "";
      const sequences = sequenceType.split(",").map((s: string) => s.trim()).filter(Boolean);

      setValue("numberofsequences", design.numberofsequences || 0);
      setValue("numberofperiods", design.period ? String(design.period) : "");
      setValue("numberoftreatments", design.numberoftreatments || 0);

      setValue("studydesigndata", {
        designtitle: design.designtitle,
        sequences: sequences,
        period: design.period,
      });
    },
  },
  {
    name: "numberoftreatments",
    label: "Number of Treatments",
    placeholder: "Enter number",
    type: "number",
    rules: validations.studyDetails.numberoftreatments,
  },
  {
    name: "numberofsequences",
    label: "Number of Sequences",
    placeholder: "Enter number",
    type: "number",
    rules: validations.studyDetails.numberofsequences,
  },
  {
    name: "numberofperiods",
    label: "Number of Periods",
    placeholder: "Select number of periods",
    type: "select",
    required: true,
    options: [],
    rules: { required: "Number of Periods is required" },
    onChangeExtra: (selected: any, { setValue }: { setValue?: any }) => {
      // Logic moved to studydesign selection
    },
  },

  {
    name: "numberofaliquots",
    label: "Number of Aliquots",
    placeholder: "Select number of aliquots",
    type: "select",
    required: true,
    options: [],
    rules: { required: "Number of Aliquots is required" },
  },

  {
    name: "fastingcondition",
    label: "Fasting / Fed Condition :",
    type: "radio",
    options: Fasting_Condition_OPTIONS,
  },

  // 5. Subject Details
  {
    name: "totalsubjects",
    label: "Total Subjects",
    placeholder: "Enter number",
    type: "number",
    required: true,
    rules: validations.studyDetails.totalsubjects,
  },
  {
    name: "extrasubjects",
    label: "Extra Subjects (Reserve / Backup) ",
    placeholder: "Enter number",
    type: "number",
    required: true,
    rules: validations.studyDetails.totalsubjects,
  },
  { name: "gender", label: "Gender", type: "select", options: Gender_OPTIONS },
  {
    name: "agemin",
    label: "Age Range (Min)",
    placeholder: "Minimum age",
    type: "number",
    rules: validations.studyDetails.agemin,
  },
  {
    name: "agemax",
    label: "Age Range (Max)",
    placeholder: "Maximum age",
    type: "number",
    rules: {
      ...validations.studyDetails.agemax,

      validate: {
        base: (value: any) =>
          validations.studyDetails.agemax?.validate?.(value),

        minMaxCheck: (value: any, formValues: any) => {
          const min = formValues?.agemin;

          if (!min || !value) return true;

          return Number(value) >= Number(min)
            ? true
            : "Max age must be greater than or equal to Min age";
        },
      },
    },
  }, {
    name: "bmimin",
    label: "BMI Range (Min)",
    placeholder: "Minimum BMI",
    type: "number",
    rules: validations.studyDetails.bmimin,
  },
  {
    name: "bmimax",
    label: "BMI Range (Max)",
    placeholder: "Maximum BMI",
    type: "number",
    rules: {
      ...validations.studyDetails.bmimax,

      validate: {
        base: (value: any) =>
          validations.studyDetails.bmimax?.validate?.(value),

        minMaxCheck: (value: any, formValues: any) => {
          const min = formValues?.bmimin;

          if (!min || !value) return true;

          return Number(value) >= Number(min)
            ? true
            : "Max BMI must be greater than or equal to Min BMI";
        },
      },
    },
  },
  // 7. Investigational Product
  {
    name: "testdrugname",
    label: "Test Drug Name",
    placeholder: "Enter test drug name",
    rules: validations.studyDetails.testdrugname,
  },
  {
    name: "testdosageform",
    label: "Dosage Form",
    placeholder: "Enter dosage form",
    rules: validations.studyDetails.testdosageform,
  },
  { name: "teststrength", label: "Strength", placeholder: "Enter strength", rules: validations.studyDetails.teststrength, },
  { name: "testbatchno", label: "Batch No", placeholder: "Enter batch number", rules: validations.studyDetails.testbatchno },
  {
    name: "testmanufacturer",
    label: "Manufacturer",
    placeholder: "Enter manufacturer",
    rules: validations.studyDetails.testmanufacturer,
  },
  { name: "testmanufacturingdate", label: "Manufacturing Date", type: "flatpickr" },
  { name: "testexpirydate", label: "Expiry Date", type: "flatpickr" },
  {
    name: "refdrugname",
    label: "Reference Drug Name",
    placeholder: "Enter reference drug name",
    rules: validations.studyDetails.refdrugname,
  },
  {
    name: "refdosageform",
    label: "Dosage Form",
    placeholder: "Enter dosage form",
    rules: validations.studyDetails.refdosageform,
  },
  { name: "refstrength", label: "Strength", placeholder: "Enter strength", rules: validations.studyDetails.refstrength, },
  { name: "refbatchno", label: "Batch No", placeholder: "Enter batch number", rules: validations.studyDetails.refbatchno, },
  {
    name: "refmanufacturer",
    label: "Manufacturer",
    placeholder: "Enter manufacturer",
    rules: validations.studyDetails.refmanufacturer,
  },
  { name: "refmanufacturingdate", label: "Manufacturing Date", type: "flatpickr" },
  { name: "refexpirydate", label: "Expiry Date", type: "flatpickr" },

  // 8. Study Timeline
  {
    name: "screeningdate",
    label: "Screening Date",
    type: "flatpickr",
  },
  {
    name: "washoutdays",
    label: "Washout Period (days)",
    placeholder: "Enter days",
    type: "number",
    required: true,
    rules: validations.studyDetails.washoutdays,
  },
  { name: "screeningvalidity", label: "Screening validity (days)", type: "number", placeholder: "Enter Screening Validity (days)", required: true, rules: validations.studyDetails.screeningvalidity, },

  // 9. Dosing Details
  {
    name: "dosestrength",
    label: "Dose Strength",
    placeholder: "Enter dose strength",
    rules: validations.studyDetails.dosestrength,
  },
  {
    name: "routeofadministration",
    label: "Route of Administration",
    type: "select",
    options: Routeof_Administration_OPTIONS,
  },
  {
    name: "dosingtime",
    label: "Dosing Time",
    placeholder: "e.g., 08:00 AM",
    type: "time",
    required: true
  }, // ✅ IMPORTANT },
  {
    name: "watervolume",
    label: "Water Volume (ml)",
    placeholder: "Enter volume in ml",
    type: "number",
    rules: validations.studyDetails.watervolume,
  },
  {
    name: "foodrestrictions",
    label: "Food Restrictions",
    placeholder: "Enter food restrictions",
    type: "textarea",
    rules: validations.studyDetails.foodrestrictions,
  },
  {
    name: "lightcondition",
    label: "Light Condition",
    type: "select",
    options: Light_Condition_OPTIONS,
  },
  {
    name: "subjectinterval",
    label: "Subject Interval (Minutes)",
    type: "text",
    placeholder: "Enter subject interval",
    required: true,
    rules: validations.studyDetails.subjectinterval,
  },
  {
    name: "predosewindowminutes",
    label: "Predose Window (Minutes)",
    placeholder: "Enter predose window in minutes",
    type: "number",
    rules: validations.studyDetails.predosewindowminutes,
  },
  // 10. Sample Collection
  {
    name: "biologicalmatrix",
    label: "Biological Matrix",
    type: "select",
    options: Biological_matrix_OPTIONS,
  },
  {
    name: "totalsamplingpoints",
    label: "Total Sampling Points",
    placeholder: "Enter number",
    type: "number",
    required: true,
    rules: validations.studyDetails.totalsamplingpoints,
  },
  {
    name: "sampleprocessingmethod",
    label: "Sample Processing Method",
    placeholder: "Enter processing method",
    rules: validations.studyDetails.sampleprocessingmethod,
  },
  {
    name: "storagetemperature",
    label: "Storage Temperature",
    placeholder: "Enter storage temperature",
    rules: validations.studyDetails.storagetemperature,
  },

  // 11. ambulatory visit
  {
    name: "visitname",
    label: "Visit Name",
    type: "text",
    required: true,
    placeholder: "e.g., Follow-up Visit 1",
    rules: validations.ambulatoryVisit.visitname
  },
  {
    name: "visittype",
    label: "Visit Type",
    type: "select",
    required: true,
    options: AMBULATORY_VISIT_TYPE_OPTIONS,
    placeholder: "Select visit type...",
  },
  {
    name: "visitcategory",
    label: "Visit Category",
    type: "select",
    required: true,
    options: AMBULATORY_VISIT_CATEGORY_OPTIONS,
    placeholder: "Select visit category...",
  },
  {
    name: "ambulatorytotalsamplingpoints",
    label: "Total Sampling Points",
    type: "number",
    required: true,
    placeholder: "Enter total sampling points (e.g. 1, 2)",
    rules: validations.ambulatoryVisit.totalsamplingpoints,
  },
  {
    name: "deviation",
    label: "Allowed Deviation Hours",
    type: "time",
    placeholder: "Select allowed deviation time",
    rules: validations.ambulatoryVisit.deviation,
  },
  {
    name: "visitdescription",
    label: "Visit Description",
    type: "textarea",
    placeholder: "Enter visit description...",
    rules: validations.ambulatoryVisit.visitdescription,
  }
];


export const getCurrentTabFields = (activeTab: number) => {
  const currentTab = tabConfig.find((tab) => tab.id === activeTab);
  const emailfields = fields.filter(
    (f) =>
      currentTab?.fields.includes(f.name as keyof StudyFormValues) &&
      f.type === "email",
  );
  return fields.filter((f) =>
    currentTab?.fields.includes(f.name as keyof StudyFormValues),
  );
};



//--------------------------------------------------------------------------------------
//--> Normalize From Value
//--------------------------------------------------------------------------------------

export const normalizeFormValues = (values: any, parentKey?: string): any => {
  // Array
  if (Array.isArray(values)) {
    return values.map((item) => normalizeFormValues(item));
  }

  // Empty
  if (values === "" || values === undefined) {
    return null;
  }

  // ✅ Handle Date object
  if (values instanceof Date) {
    return format(values, "yyyy-MM-dd");
  }

  // ✅ Handle string (STRICT)
  if (typeof values === "string") {
    const trimmed = values.trim();

    // ✅ Only parse if it matches ISO pattern
    const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;

    if (ISO_DATE_REGEX.test(trimmed)) {
      const parsedDate = parseISO(trimmed);

      if (isValid(parsedDate)) {
        return format(parsedDate, "yyyy-MM-dd");
      }
    }

    return trimmed; // "85" stays "85" ✅
  }

  // Object (recursive)
  if (typeof values === "object" && values !== null) {
    const normalized: any = {};
    Object.entries(values).forEach(([key, value]) => {
      normalized[key] = normalizeFormValues(value);
    });
    return normalized;
  }

  return values;
};



export const tabConfig: TabConfig[] = [
  {
    id: 1,
    title: "Study Identification",
    icon: "ri-file-text-line",
    fields: ["studytitle", "studycode", "versionnumber", "protocolversiondate"],
  },
  {
    id: 2,
    title: "Sponsor & CRO Details",
    icon: "ri-building-line",
    fields: ["sponsorname", "croname"],
  },
  {
    id: 3,
    title: "Regulatory & Ethics",
    icon: "ri-shield-check-line",
    fields: [
      "ethicscommitteename",
      "ethicsapprovaldate",
      "regulatoryauthority",
    ],
  },
  {
    id: 4,
    title: "Study Design",
    icon: "ri-layout-grid-line",
    fields: [
      "studytype",
      "numberoftreatments",
      "numberofperiods",
      "studydesign",
      "numberofaliquots",
      "numberofsequences",
      "fastingcondition",
    ],
  },
  {
    id: 5,
    title: "Checklist Assignment",
    icon: "ri-checkbox-multiple-line",
    fields: [],
  },
  {
    id: 6,
    title: "CRF Assignment",
    icon: "ri-task-line",
    fields: [],
  },
  {
    id: 7,
    title: "Subject Details",
    icon: "ri-user-line",
    fields: ["totalsubjects", "extrasubjects", "gender", "agemin", "agemax", "bmimin", "bmimax"],
  },
  {
    id: 8,
    title: "Investigational Product",
    icon: "ri-medicine-bottle-line",
    fields: [
      "testdrugname",
      "testdosageform",
      "teststrength",
      "testbatchno",
      "testmanufacturer",
      "testmanufacturingdate",
      "testexpirydate",
      "refdrugname",
      "refdosageform",
      "refstrength",
      "refbatchno",
      "refmanufacturer",
      "refmanufacturingdate",
      "refexpirydate",
    ],
  },

  {
    id: 9,
    title: "Study Timeline",
    icon: "ri-calendar-event-line",
    fields: [
      "screeningdate",
      "screeningvalidity",
      "washoutdays",
      "periods",
    ],
  },
  {
    id: 10,
    title: "Dosing Details",
    icon: "ri-capsule-line",
    fields: [
      "dosestrength",
      "routeofadministration",
      "dosingtime",
      "watervolume",
      "foodrestrictions",
      "lightcondition",
      "subjectinterval",
      "predosewindowminutes",
    ],
  },
  {
    id: 11,
    title: "Sample Collection",
    icon: "ri-test-tube-line",
    fields: [
      "biologicalmatrix",
      "totalsamplingpoints",
      "sampleprocessingmethod",
      "storagetemperature",
      "samplingpoints",
    ],
  },
  {
    id: 12,
    title: "Ambulatory Visit",
    icon: "ri-walk-line",
    fields: [
      "visitname",
      "visittype",
      "visitcategory",
      "ambulatorytotalsamplingpoints",
      "deviation",
      "visitdescription"
    ],
  },
];



export const getFieldsBySection = (
  activeSection: StudySection,
): FormFieldConfig[] => {
  switch (activeSection) {
    case "Study Identification":
      return [
        {
          name: "studytitle",
          label: "Study Title",
          placeholder: "Enter study title",
          rules: validations.studyDetails.studytitle,
        },
        {
          name: "studycode",
          label: "Study Code / ID",
          placeholder: "Enter study code",
          required: true,
          rules: validations.studyDetails.studycode,
        },
        {
          name: "versionnumber",
          label: "Version Number",
          placeholder: "Enter version number",
          rules: validations.studyDetails.versionnumber,
        },
        {
          name: "protocolversiondate",
          label: "Protocol Version Date",
          type: "flatpickr",
        },
      ];

    case "Sponsor & CRO Details":
      return [
        {
          name: "sponsorname",
          label: "Sponsor Name",
          placeholder: "Select sponsor name",
          required: true,
          type: "select",
          options: SPONSOR_NAME_OPTIONS,
          rules: validations.studyDetails.sponsorname,
          onChangeExtra: (
            selected: any,
            { setValue }: { setValue?: any },
          ) => {
            const sponsor = selected?.data;
            if (!sponsor) {
              setValue("croname", "");
              return;
            }
            setValue("croname", sponsor.croname);
            setValue("sponsorid", sponsor.sponsorid);
          },
        },
        {
          name: "croname",
          label: "CRO Name",
          disabled: true,
        },
      ];

    case "Regulatory & Ethics":
      return [
        {
          name: "ethicscommitteename",
          label: "Ethics Committee Name",
          placeholder: "Enter ethics committee name",
          rules: validations.studyDetails.ethicscommitteename,
        },
        {
          name: "ethicsapprovaldate",
          label: "Ethics Approval Date",
          type: "flatpickr",
        },
        {
          name: "regulatoryauthority",
          label: "Regulatory Authority",
          placeholder: "Enter regulatory authority",
          rules: validations.studyDetails.regulatoryauthority,
        },
      ];

    case "Study Design":
      return [
        {
          name: "studytype",
          label: "Study Type",
          type: "select",
          options: StudyType_OPTIONS,
          required: true,
          rules: { required: "Study Type is required" },
        },
        {
          name: "studydesign",
          label: "Study Design",
          placeholder: "Select study design",
          type: "select",
          options: StudyDesign_OPTIONS,
          required: true,
          onChangeExtra: (selected: any, { setValue }: { setValue?: any }) => {
            const design = selected?.data;
            if (!design) {
              setValue("studydesigndata", null);
              setValue("numberofsequences", "");
              setValue("numberofperiods", "");
              setValue("numberoftreatments", "");
              return;
            }
            setValue("numberofsequences", design.numberofsequences || 0);
            setValue("numberofperiods", design.period ? String(design.period) : "");
            setValue("numberoftreatments", design.numberoftreatments || 0);
          },
        },
        {
          name: "numberoftreatments",
          label: "Number of Treatments",
          placeholder: "Enter number",
          type: "number",
          rules: validations.studyDetails.numberoftreatments,
        },
        {
          name: "numberofsequences",
          label: "Number of Sequences",
          placeholder: "Enter number",
          type: "number",
          rules: validations.studyDetails.numberofsequences,
        },
        {
          name: "numberofperiods",
          label: "Number of Periods",
          placeholder: "Select number of periods",
          type: "select",
          required: true,
          options: [],
          rules: { required: "Number of Periods is required" },
          onChangeExtra: (selected: any, { setValue }: { setValue?: any }) => {
            setValue("studydesign", "");
            setValue("studydesigndata", null);
          },
        },

        {
          name: "numberofaliquots",
          label: "Number of Aliquots",
          placeholder: "Select number of aliquots",
          type: "select",
          required: true,
          options: [],
          rules: { required: "Number of Aliquots is required" },
        },

        {
          name: "fastingcondition",
          label: "Fasting / Fed Condition :",
          type: "radio",
          options: Fasting_Condition_OPTIONS,
        },
      ];

    case "Subject Details":
      return [
        {
          name: "totalsubjects",
          label: "Total Subjects ",
          placeholder: "Enter number",
          type: "number",
          required: true,
          rules: validations.studyDetails.totalsubjects,
        },
        {
          name: "extrasubjects",
          label: "Extra Subjects (Reserve / Backup) ",
          placeholder: "Enter number",
          type: "number",
          required: true,
          rules: validations.studyDetails.totalsubjects,
        },
        {
          name: "gender",
          label: "Gender",
          type: "select",
          options: Gender_OPTIONS,
        },
        {
          name: "agemin",
          label: "Age Range (Min)",
          placeholder: "Minimum age",
          type: "number",
          rules: validations.studyDetails.agemin,
        },
        {
          name: "agemax",
          label: "Age Range (Max)",
          placeholder: "Maximum age",
          type: "number",
          rules: {
            ...validations.studyDetails.agemax,

            validate: {
              base: (value: any) =>
                validations.studyDetails.agemax?.validate?.(value),

              minMaxCheck: (value: any, formValues: any) => {
                const min = formValues?.agemin;

                if (!min || !value) return true;

                return Number(value) >= Number(min)
                  ? true
                  : "Max age must be greater than or equal to Min age";
              },
            },
          }
        },
        {
          name: "bmimin",
          label: "BMI Range (Min)",
          placeholder: "Minimum BMI",
          type: "number",
          rules: validations.studyDetails.bmimin,
        },
        {
          name: "bmimax",
          label: "BMI Range (Max)",
          placeholder: "Maximum BMI",
          type: "number",
          rules: {
            ...validations.studyDetails.bmimax,

            validate: {
              base: (value: any) =>
                validations.studyDetails.bmimax?.validate?.(value),

              minMaxCheck: (value: any, formValues: any) => {
                const min = formValues?.bmimin;

                if (!min || !value) return true;

                return Number(value) >= Number(min)
                  ? true
                  : "Max BMI must be greater than or equal to Min BMI";
              },
            },
          }
        },
      ];

    case "Investigational Product":
      return [
        {
          name: "testdrugname",
          label: "Test Drug Name",
          placeholder: "Enter test drug name",
          rules: validations.studyDetails.testdrugname,
        },
        {
          name: "testdosageform",
          label: "Dosage Form",
          placeholder: "Enter dosage form",
          rules: validations.studyDetails.testdosageform,
        },
        {
          name: "teststrength",
          label: "Strength",
          placeholder: "Enter strength",
          rules: validations.studyDetails.teststrength,
        },
        {
          name: "testbatchno",
          label: "Batch No",
          placeholder: "Enter batch number",
          rules: validations.studyDetails.testbatchno,
        },
        {
          name: "testmanufacturer",
          label: "Manufacturer",
          placeholder: "Enter manufacturer",
          rules: validations.studyDetails.testmanufacturer,
        },
        {
          name: "testmanufacturingdate",
          label: "Manufacturing Date",
          type: "flatpickr",
        },
        {
          name: "testexpirydate",
          label: "Expiry Date",
          type: "flatpickr",
        },
        // Reference Product
        {
          name: "refdrugname",
          label: "Reference Drug Name",
          placeholder: "Enter reference drug name",
          rules: validations.studyDetails.refdrugname,
        },
        {
          name: "refdosageform",
          label: "Dosage Form",
          placeholder: "Enter dosage form",
          rules: validations.studyDetails.refdosageform,
        },
        {
          name: "refstrength",
          label: "Strength",
          placeholder: "Enter strength",
          rules: validations.studyDetails.refstrength,
        },
        {
          name: "refbatchno",
          label: "Batch No",
          placeholder: "Enter batch number",
          rules: validations.studyDetails.refbatchno,
        },
        {
          name: "refmanufacturer",
          label: "Manufacturer",
          placeholder: "Enter manufacturer",
          rules: validations.studyDetails.refmanufacturer,
        },
        {
          name: "refmanufacturingdate",
          label: "Manufacturing Date",
          type: "flatpickr",
        },
        {
          name: "refexpirydate",
          label: "Expiry Date",
          type: "flatpickr",
        },
      ];

    case "Study Timeline":
      return [
        {
          name: "screeningdate",
          label: "Screening Date",
          type: "flatpickr",
        },
        {
          name: "screeningvalidity",
          label: "Screening Validity (days)",
          placeholder: "Enter Screening Validity (days)",
          type: "number",
          rules: validations.studyDetails.screeningvalidity,
        },
        {
          name: "washoutdays",
          label: "Washout Period (days)",
          placeholder: "Enter days",
          type: "number",
          required: true,
          rules: validations.studyDetails.washoutdays,
        },
      ];

    case "Dosing Details":
      return [
        {
          name: "dosestrength",
          label: "Dose Strength",
          placeholder: "Enter dose strength",
          rules: validations.studyDetails.dosestrength,
        },
        {
          name: "routeofadministration",
          label: "Route of Administration",
          type: "select",
          options: Routeof_Administration_OPTIONS,
        },
        {
          name: "dosingtime",
          label: "Dosing Time",
          placeholder: "e.g., 08:00 AM",
          type: "time",
        },
        {
          name: "watervolume",
          label: "Water Volume (ml)",
          placeholder: "Enter volume in ml",
          type: "number",
          rules: validations.studyDetails.watervolume,
        },
        {
          name: "foodrestrictions",
          label: "Food Restrictions",
          placeholder: "Enter food restrictions",
          type: "textarea",
          rules: validations.studyDetails.foodrestrictions,
        },
        {
          name: "lightcondition",
          label: "Light Condition",
          type: "select",
          options: Light_Condition_OPTIONS,
        },
        {
          name: "subjectinterval",
          label: "Subject Interval (Minutes)",
          placeholder: "Enter Subject Interval",
          required: true,
          rules: validations.studyDetails.subjectinterval,
        },
        {
          name: "predosewindowminutes",
          label: "Predose Window (Minutes)",
          placeholder: "Enter predose window in minutes",
          type: "number",
          rules: validations.studyDetails.predosewindowminutes,
        },
      ];

    case "Sample Collection":
      return [
        {
          name: "biologicalmatrix",
          label: "Biological Matrix",
          type: "select",
          options: Biological_matrix_OPTIONS,
        },
        {
          name: "totalsamplingpoints",
          label: "Total Sampling Points",
          placeholder: "Enter number",
          type: "number",
          required: true,
          rules: validations.studyDetails.totalsamplingpoints,
        },
        {
          name: "sampleprocessingmethod",
          label: "Sample Processing Method",
          placeholder: "Enter processing method",
          rules: validations.studyDetails.sampleprocessingmethod,
        },
        {
          name: "storagetemperature",
          label: "Storage Temperature",
          placeholder: "Enter storage temperature",
          rules: validations.studyDetails.storagetemperature,
        },
      ];

    default:
      return [];
  }
};


type LabelMap = Partial<Record<keyof StudyFormValues, string>>;

export const labels: LabelMap = {
  // 1. Study Identification
  studytitle: "Study Title",
  studycode: "Study Code / ID",
  versionnumber: "Version Number",
  protocolversiondate: "Protocol Version Date",
  sponsorname: "Sponsor Name",
  croname: "CRO Name",
  ethicscommitteename: "Ethics Committee Name",
  ethicsapprovaldate: "Ethics Approval Date",
  regulatoryauthority: "Regulatory Authority",
  studydesign: "Study Design",
  studytype: "Study Type",
  numberoftreatments: "Number of Treatments",
  numberofperiods: "Number of Periods",
  numberofsequences: "Number of Sequences",
  numberofaliquots: "Number of Aliquots",
  washoutdays: "Washout Period (days)",
  fastingcondition: "Fasting / Fed Condition",
  totalsubjects: "Total Subjects ",
  extrasubjects: "Extra Subjects",
  gender: "Gender",
  agemin: "Age Range (Min)",
  agemax: "Age Range (Max)",
  bmimin: "BMI Range (Min)",
  bmimax: "BMI Range (Max)",
  testdrugname: "Test Drug Name",
  testdosageform: "Dosage Form",
  teststrength: "Strength",
  testbatchno: "Batch No",
  testmanufacturer: "Manufacturer",
  testmanufacturingdate: "Manufacturing Date",
  testexpirydate: "Expiry Date",
  refdrugname: "Reference Drug Name",
  refdosageform: "Dosage Form",
  refstrength: "Strength",
  refbatchno: "Batch No",
  refmanufacturer: "Manufacturer",
  refmanufacturingdate: "Manufacturing Date",
  refexpirydate: "Expiry Date",
  dosestrength: "Dose Strength",
  routeofadministration: "Route of Administration",
  dosingtime: "Dosing Time",
  watervolume: "Water Volume (ml)",
  foodrestrictions: "Food Restrictions",
  lightcondition: "Light Condition",
  subjectinterval: "Subject Interval",
  predosewindowminutes: "Predose Window (Minutes)",

  screeningdate: "Screening Date",
  screeningvalidity: "Screening Validity (days)",
  biologicalmatrix: "Biological Matrix",
  totalsamplingpoints: "Total Sampling Points",
  sampleprocessingmethod: "Sample Processing Method",
  storagetemperature: "Storage Temperature",
  samplingpoints: "Sampling Points",
};


export const timelineFieldNames: Array<keyof StudyFormValues> = [
  "screeningdate",
  "screeningvalidity",
  "washoutdays",
];



export const decimalFields: (keyof StudyFormValues)[] = ["bmimin", "bmimax"];
export const numberFields: (keyof StudyFormValues)[] = [
  "numberoftreatments",
  "numberofsequences",
  "washoutdays",
  "totalsubjects",
  "extrasubjects",
  "agemin",
  "agemax",
  "watervolume",
  "totalsamplingpoints",
  "predosewindowminutes",
];


export const totalStudyDurationText = (values: any) => {
  const checkInDates = [
    values.period1checkindate,
    values.period2checkindate,
    values.period3checkindate,
    values.period4checkindate,
  ]
    .filter(Boolean)
    .map((d: string) => new Date(d));

  const checkOutDates = [
    values.period1checkoutdate,
    values.period2checkoutdate,
    values.period3checkoutdate,
    values.period4checkoutdate,
  ]
    .filter(Boolean)
    .map((d: string) => new Date(d));

  if (!checkInDates.length || !checkOutDates.length) return "";

  const earliestCheckIn = new Date(
    Math.min(...checkInDates.map((d) => d.getTime()))
  );

  const latestCheckOut = new Date(
    Math.max(...checkOutDates.map((d) => d.getTime()))
  );

  const totalDays =
    Math.floor(
      (latestCheckOut.getTime() - earliestCheckIn.getTime()) /
      (24 * 60 * 60 * 1000)
    ) + 1;

  return `${totalDays} day${totalDays > 1 ? "s" : ""}`;
};


export const normalizeStudyForm = (data: StudyFormValues) => {
  const updated = { ...data };

  numberFields.forEach((key) => {
    const val = updated[key];
    if (val !== undefined && val !== null && val !== "") {
      const num = Number(val);
      (updated as any)[key] = Number.isFinite(num) ? num : null;
    }

  });

  decimalFields.forEach((key) => {
    const val = updated[key];
    if (val !== undefined && val !== null && val !== "") {
      const num = parseFloat(String(val));
      (updated as any)[key] = Number.isFinite(num) ? num : null;
    }
  });

  return updated;
};


export const getPeriodFields = (count: number | null): FormFieldConfig[] => {
  if (!count) return [];

  return Array.from({ length: count }, (_, i) => {
    const index = i + 1;

    return [
      {
        name: `period${index}startdate`,
        label: `Period ${index} Start Date`,
        type: "flatpickr" as FieldType,
        required: true,
      },
      {
        name: `period${index}checkindate`,
        label: `Period ${index} Check-in Date`,
        type: "flatpickr" as FieldType,
        required: true,
      },
      {
        name: `period${index}checkoutdate`,
        label: `Period ${index} Check-out Date`,
        type: "flatpickr" as FieldType,
        required: true,
      },
    ];
  }).flat();
};


///////////////// Study List commmon //////////////////////////

export const getStudyListColumns = (navigate: any): Column[] => [
  {
    key: "studycode",
    label: "Study Code",
    width: "140px",
    type: "link",
    onClick: (row: Study) => {
      sessionStorage.setItem("activestudyid", String(row.studyid));
      navigate("/master/study/details/*");
    },
  },
  {
    key: "studytitle",
    label: "Study Title",
    maxLength: 40,
  },
  {
    key: "sponsorname",
    label: "Sponsor",
    tooltip: true,
    maxLength: 30,
    width: "200px",
  },
  {
    key: "studydesign",
    label: "Design",
    width: "120px",
    render: (studyDesign: number) =>
      getLabelFromValue(StudyDesign_OPTIONS, studyDesign),
  },
  {
    key: "totalsubjects",
    label: "Subjects",
    width: "100px",
  },
  {
    key: "numberofperiods",
    label: "Periods",
    width: "90px",
  },
];


const parseDate = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;

  // ISO format
  if (value.includes("T")) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  // DD-MM-YYYY format
  const [day, month, year] = value.split("-").map(Number);
  if (!day || !month || !year) return null;

  return new Date(year, month - 1, day);
};

export const visitformfields = [
  {
    key: "visitname",
    label: "VISIT NAME",
  },
  {
    key: "visittype",
    label: "VISIT TYPE",
    render: (val: any) => {
      const opt = AMBULATORY_VISIT_TYPE_OPTIONS.find(o => String(o.value) === String(val));
      return opt ? opt.label : val || "--";
    }
  },
  {
    key: "visitcategory",
    label: "VISIT CATEGORY",
    render: (val: any) => {
      const opt = AMBULATORY_VISIT_CATEGORY_OPTIONS.find(o => String(o.value) === String(val));
      return opt ? opt.label : val || "--";
    }
  },
  {
    key: "samplingpoints",
    label: "SAMPLING POINTS",
    render: (val: any) => {
      if (Array.isArray(val)) return val.join(", ");
      return val || "--";
    }
  },
  {
    key: "deviation",
    label: "ALLOWED DEVIATION TIME",
    render: (val: any) => val || "--"
  },
  {
    key: "visitdescription",
    label: "VISIT DESCRIPTION",
    render: (val: any) => val || "--"
  }
];

export const periodColumns = (watch: any, control: any, errors: any, trigger: any): Column[] => {
  return [
    {
      key: "period",
      label: "Period",
    },
    {
      key: "start",
      label: "Start Date *",
      render: (_: any, row: any) => {
        let minDateOpt = undefined;
        let isStartDisabled = false;

        if (row.period > 1) {
          const prevCheckout = watch(`period${row.period - 1}checkoutdate`);
          const washoutDaysStr = watch("washoutdays");
          const washoutDays = Number(washoutDaysStr) || 0;

          if (prevCheckout) {
            const minDate = parseLocalDate(prevCheckout);
            if (minDate && !isNaN(minDate.getTime())) {
              minDate.setDate(minDate.getDate() + washoutDays + 1);
              minDateOpt = minDate;
            }
          } else {
            // Force user to fill out previous period's checkout date first
            isStartDisabled = true;
          }
        }

        return (
          <CommonFlatpickr
            control={control}
            name={`period${row.period}startdate`}
            errors={errors}
            rules={{ required: true }}
            showerror={trigger}
            className="modified_input"
            position={true}
            disabled={isStartDisabled}
            options={minDateOpt ? { minDate: minDateOpt } : {}}
          />
        );
      },
    },
    {
      key: "checkin",
      label: "Check-in Date * ",
      render: (_: any, row: any) => {
        const startDateVal = watch(`period${row.period}startdate`);
        let maxDateOpt = undefined;

        if (startDateVal) {
          const maxDate = parseLocalDate(startDateVal);
          if (maxDate && !isNaN(maxDate.getTime())) {
            maxDate.setDate(maxDate.getDate() - 1);
            maxDateOpt = maxDate;
          }
        }

        return (
          <CommonFlatpickr
            control={control}
            name={`period${row.period}checkindate`}
            errors={errors}
            rules={{ required: true }}
            showerror={trigger}
            disabled={!startDateVal}
            options={maxDateOpt ? { maxDate: maxDateOpt } : {}}
            className="modified_input"
            position={true}
          />
        );
      },
    },
    {
      key: "checkout",
      label: "Check-out Date *",
      render: (_: any, row: any) => {
        const startDateVal = watch(`period${row.period}startdate`);
        const minDateOpt = parseLocalDate(startDateVal);

        return (
          <CommonFlatpickr
            control={control}
            name={`period${row.period}checkoutdate`}
            errors={errors}
            rules={{ required: true }}
            showerror={trigger}
            disabled={!startDateVal}
            options={minDateOpt ? { minDate: minDateOpt } : {}}
            className="modified_input"
            position={true}
          />
        );
      },
    },
  ];
}

export const testDrugFieldNames = [
  "testdrugname",
  "testdosageform",
  "teststrength",
  "testbatchno",
  "testmanufacturer",
  "testmanufacturingdate",
  "testexpirydate",
];

export const refDrugFieldNames = [
  "refdrugname",
  "refdosageform",
  "refstrength",
  "refbatchno",
  "refmanufacturer",
  "refmanufacturingdate",
  "refexpirydate",
];

export const dynamicFields = (agemin: any, bmimin: any, displaySequenceOptions: any, displayAliquoteSequenceOptions: any, activeTab: any) => getCurrentTabFields(activeTab).map((field) => {
  if (field.name === "agemax") {
    return {
      ...field,
      disabled: !agemin,
    };
  }
  if (field.name === "bmimax") {
    return {
      ...field,
      disabled: !bmimin,
    };
  }
  if (field.name === "numberofperiods") {
    return {
      ...field,
      options: displaySequenceOptions,
      disabled: true,
    };
  }
  if (field.name === "studydesign") {
    return {
      ...field,
      disabled: false,
    };
  }
  if (
    ["numberoftreatments", "numberofsequences", "numberofperiods"].includes(
      field.name as string,
    )
  ) {
    return {
      ...field,
      disabled: true,
    };
  }
  if (field.name === "numberofaliquots") {
    return {
      ...field,
      options: displayAliquoteSequenceOptions,
    };
  }
  return field;
});

export const periodColumnsEdit = (watch: any, control: any, errors: any, trigger: any) => [
  {
    key: "period",
    label: "Period",
    render: (_: any, row: any) => row.index + 1,
  },
  {
    key: "start",
    label: "Start Date *",
    render: (_: any, row: any) => {
      let minDateOpt = undefined;
      let isStartDisabled = false;

      if (row.index > 0) {
        const prevCheckout = watch(`periods.${row.index - 1}.checkout_date`);
        const washoutDaysStr = watch("washoutdays");
        const washoutDays = Number(washoutDaysStr) || 0;

        if (prevCheckout) {
          const minDate = parseLocalDate(prevCheckout);
          if (minDate && !isNaN(minDate.getTime())) {
            minDate.setDate(minDate.getDate() + washoutDays + 1);
            minDateOpt = minDate;
          }
        } else {
          isStartDisabled = true;
        }
      }

      return (
        <CommonFlatpickr
          control={control}
          name={`periods.${row.index}.start_date`}
          errors={errors}
          showerror={trigger}
          rules={{ required: true }}
          className="modified_input"
          position={true}
          disabled={isStartDisabled}
          options={minDateOpt ? { minDate: minDateOpt } : {}}
        />
      );
    },
  },
  {
    key: "checkin",
    label: "Check-in Date *",
    render: (_: any, row: any) => {
      const startDateVal = watch(`periods.${row.index}.start_date`);
      let maxDateOpt = undefined;

      if (startDateVal) {
        const maxDate = parseLocalDate(startDateVal);
        if (maxDate && !isNaN(maxDate.getTime())) {
          maxDate.setDate(maxDate.getDate() - 1);
          maxDateOpt = maxDate;
        }
      }

      return (
        <CommonFlatpickr
          control={control}
          name={`periods.${row.index}.checkin_date`}
          errors={errors}
          rules={{ required: true }}
          showerror={trigger}
          disabled={!startDateVal}
          options={maxDateOpt ? { maxDate: maxDateOpt } : {}}
          className="modified_input"
          position={true}
        />
      );
    },
  },
  {
    key: "checkout",
    label: "Check-out Date *",
    render: (_: any, row: any) => {
      const startDateVal = watch(`periods.${row.index}.start_date`);
      const minDateOpt = parseLocalDate(startDateVal);

      return (
        <CommonFlatpickr
          control={control}
          name={`periods.${row.index}.checkout_date`}
          errors={errors}
          showerror={trigger}
          rules={{ required: true }}
          disabled={!startDateVal} // ✅ disable until start date
          options={minDateOpt ? { minDate: minDateOpt } : {}}
          className="modified_input"
          position={true}
        />
      );
    },
  },
];

export const dynamicFieldsEdit = (agemin: any, bmimin: any, activeSection: any, displaySequenceOptions: any, displayAliquoteSequenceOptions: any, testmanufacturingdate: any, refmanufacturingdate: any) => getFieldsBySection(activeSection).map((field) => {
  if (field.name === "agemax") {
    return {
      ...field,
      disabled: !agemin,
    };
  }
  if (field.name === "bmimax") {
    return {
      ...field,
      disabled: !bmimin,
    };
  }
  if (field.name === "numberofperiods") {
    return {
      ...field,
      options: displaySequenceOptions,
      disabled: true,
    };
  }
  if (field.name === "studydesign") {
    return {
      ...field,
      disabled: false,
    };
  }
  if (
    ["numberoftreatments", "numberofsequences", "numberofperiods"].includes(
      field.name as string,
    )
  ) {
    return {
      ...field,
      disabled: true,
    };
  }
  if (field.name === "numberofaliquots") {
    return {
      ...field,
      options: displayAliquoteSequenceOptions,
    };
  }
  if (field.name === "testexpirydate" && testmanufacturingdate) {
    return {
      ...field,
      optionsProps: { minDate: parseLocalDate(testmanufacturingdate) ?? undefined },
    };
  }
  if (field.name === "refexpirydate" && refmanufacturingdate) {
    return {
      ...field,
      optionsProps: { minDate: parseLocalDate(refmanufacturingdate) ?? undefined },
    };
  }
  return field;
});


export const getChangedFields = (currentValues: any, originalValues: any) => {
  const changedFields: Record<string, any> = {};

  Object.keys(currentValues || {}).forEach((key) => {
    const currentValue = currentValues[key];
    const originalValue = originalValues?.[key];

    const hasChanged =
      JSON.stringify(currentValue) !== JSON.stringify(originalValue);

    if (hasChanged) {
      changedFields[key] = currentValue;
    }
  });

  return changedFields;
};
//-------------------------------------------------------------------------------------------
//--> Checklist Assignment Helpers
//-------------------------------------------------------------------------------------------

export interface ChecklistTypeOption {
  value: number;
  label: string;
}

export const getActiveChecklistTypes = (rawApiData: any[]): ChecklistTypeOption[] => {
  if (!Array.isArray(rawApiData) || rawApiData.length === 0) return [];

  const typeMap = new Map<number, string>();
  rawApiData.forEach((item: any) => {
    const typeVal = Number(item.checklisttype);
    const typeText = String(
      item.checklisttypename ||
      item.checklisttype_name ||
      item.checklisttypetext ||
      item.checklisttype_text ||
      ""
    );
    if (typeVal && !typeMap.has(typeVal)) {
      typeMap.set(typeVal, typeText);
    }
  });

  return Array.from(typeMap.entries()).map(([value, label]) => ({
    value,
    label,
  }));
};

export const getSelectedTypeLabel = (
  activeChecklistTypes: ChecklistTypeOption[],
  selectedType?: Number | number
): string => {
  if (!selectedType) return "";
  const found = activeChecklistTypes.find((t) => t.value === Number(selectedType));
  return found ? found.label : String(selectedType);
};

export const getChecklistsForKey = (
  customChecklists: Record<string, any[]>,
  rawApiData: any[],
  key: string,
  typeVal: number
) => {
  if (customChecklists[key]) return customChecklists[key];
  if (!Array.isArray(rawApiData)) return [];
  return rawApiData
    .filter((item: any) => Number(item.checklisttype) === typeVal)
    .map((item: any) => ({ ...item, assigned: true }));
};

export const getCountForType = (
  mode: "common" | "period_specific",
  selectedPeriod: number,
  typeVal: number,
  customChecklists: Record<string, any[]>,
  rawApiData: any[]
): number => {
  const key = mode === "period_specific" ? `p${selectedPeriod}_${typeVal}` : `common_${typeVal}`;
  if (customChecklists[key]) {
    return customChecklists[key].length;
  }
  if (!Array.isArray(rawApiData)) return 0;
  return rawApiData.filter((item: any) => Number(item.checklisttype) === Number(typeVal)).length;
};

export const executeCopyFromPeriod = (
  srcPeriod: number,
  selectedPeriod: number,
  selectedType: number,
  selectedTypeLabel: string,
  customChecklists: Record<string, any[]>,
  assignedState: Record<string, Record<number, boolean>>,
  rawApiData: any[]
): {
  success: boolean;
  newCustomChecklists?: Record<string, any[]>;
  newAssignedState?: Record<string, Record<number, boolean>>;
  message: string;
} => {
  const srcKey = `p${srcPeriod}_${selectedType}`;
  const dstKey = `p${selectedPeriod}_${selectedType}`;

  const sourceItems = getChecklistsForKey(customChecklists, rawApiData, srcKey, selectedType).map(
    (item: any) => ({
      ...item,
      masterchecklistid: item.isCustom ? Date.now() + Math.random() : item.masterchecklistid,
      assigned: true,
    })
  );

  if (sourceItems.length === 0) {
    return {
      success: false,
      message: `No checklists found in Period ${srcPeriod} for ${selectedTypeLabel}.`,
    };
  }

  const srcAssigned = assignedState[srcKey] || {};

  return {
    success: true,
    newCustomChecklists: {
      ...customChecklists,
      [dstKey]: sourceItems,
    },
    newAssignedState: {
      ...assignedState,
      [dstKey]: { ...srcAssigned },
    },
    message: `Copied ${sourceItems.length} checklist(s) from Period ${srcPeriod} to Period ${selectedPeriod}.`,
  };
};

export const normalizeStudyChecklists = (raw: any): any[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    const before = Array.isArray(raw.beforeenrollment) ? raw.beforeenrollment : [];
    const after = Array.isArray(raw.afterenrollment) ? raw.afterenrollment : [];
    const afterItems = after.flatMap((p: any) => {
      const pId = Number(p.periodid || p.period_no || p.period_number) || 1;
      return (Array.isArray(p.checklists) ? p.checklists : []).map((c: any) => ({
        ...c,
        periodid: pId,
        period_sort: pId,
      }));
    });
    return [...before, ...afterItems];
  }
  return [];
};

export const buildStudyChecklistsPayload = (formValues: any, rawApiData: any[] = []) => {
  const numPeriods = Number(formValues?.numberofperiods) || 1;
  const config = formValues?.configuredChecklistsData || {};
  const {
    assignedState = {},
    customChecklists = {},
    hiddenTypeValuesState = {},
    isEditMode = false,
    existingStudyChecklists = null,
  } = config;

  const savedContextMap: Record<
    string,
    { masterIds: Set<number>; customItems: any[]; savedMasterMap: Map<number, any> }
  > = {};

  const flatExisting = normalizeStudyChecklists(existingStudyChecklists);
  if (flatExisting.length > 0) {
    flatExisting.forEach((item: any) => {
      const periodVal = Number(item.period_sort || item.periodid || item.period_no) || 1;
      const typeVal = Number(item.checklisttype);

      const periodKey = `p${periodVal}_${typeVal}`;
      if (!savedContextMap[periodKey]) {
        savedContextMap[periodKey] = {
          masterIds: new Set<number>(),
          customItems: [],
          savedMasterMap: new Map<number, any>(),
        };
      }

      const target = savedContextMap[periodKey];
      const hasMaster = item.masterchecklistid != null && Number(item.masterchecklistid) !== 0;

      if (hasMaster) {
        const masterId = Number(item.masterchecklistid);
        if (!target.masterIds.has(masterId)) {
          target.masterIds.add(masterId);
          target.savedMasterMap.set(masterId, item);
        }
      } else {
        const qClean = String(item.question || "").trim().toLowerCase();
        const alreadyExists = target.customItems.some(
          (c) => String(c.question || "").trim().toLowerCase() === qClean
        );

        if (!alreadyExists) {
          const customId = Number(item.studychecklistid) || item.id;
          target.customItems.push({
            ...item,
            id: customId,
            studychecklistid: Number(item.studychecklistid) || null,
            masterchecklistid: null,
            isCustom: true,
            assigned: true,
          });
        }
      }
    });
  }

  const typeMap = new Map<number, string>();
  const categoryMap = new Map<number, any>();

  if (Array.isArray(rawApiData)) {
    rawApiData.forEach((item: any) => {
      const typeVal = Number(item.checklisttype);
      if (typeVal && !typeMap.has(typeVal)) {
        typeMap.set(
          typeVal,
          String(
            item.checklisttypetext ||
            item.checklisttypename ||
            item.checklisttype_text ||
            ""
          )
        );
      }
      if (typeVal && item.checklistcategory && !categoryMap.has(typeVal)) {
        categoryMap.set(typeVal, item.checklistcategory);
      }
    });
  }
  const allTypeIds = Array.from(typeMap.keys());

  const isPreEnrollmentType = (typeVal: number) => {
    const text = (typeMap.get(typeVal) || "").toLowerCase();
    return (
      text.includes("inclusion") ||
      text.includes("exclusion") ||
      text.includes("pre enrollment") ||
      text.includes("pre-enrollment")
    );
  };

  const beforeenrollment: any[] = [];
  const afterenrollment: any[] = [];

  let globalPreOrder = 1;

  const hiddenPreList = [
    ...(hiddenTypeValuesState["p1"] || []),
    ...(hiddenTypeValuesState["pre_enrollment"] || []),
    ...(hiddenTypeValuesState["common"] || []),
  ];
  const hiddenPreSet = new Set(hiddenPreList.map(Number));

  // 1. Process Pre-Enrollment Checklists (beforeenrollment)
  allTypeIds.forEach((typeVal) => {
    if (!isPreEnrollmentType(typeVal) || hiddenPreSet.has(typeVal)) return;

    const key = `p1_${typeVal}`;
    const currentMap = assignedState[key] || {};
    const savedContext = savedContextMap[key];

    let items: any[] = [];
    if (customChecklists[key]) {
      items = customChecklists[key];
    } else if (Array.isArray(rawApiData)) {
      const masterItems = rawApiData.filter(
        (item: any) => Number(item.checklisttype) === typeVal
      );
      if (isEditMode) {
        const mappedMasters = masterItems.map((masterItem: any) => {
          const masterId = Number(masterItem.masterchecklistid || masterItem.id);
          const savedItem = savedContext?.savedMasterMap.get(masterId);
          if (savedItem) {
            return {
              ...masterItem,
              studychecklistid: Number(savedItem.studychecklistid) || null,
              question: savedItem.question || masterItem.question,
              isrequired:
                savedItem.isrequired !== undefined
                  ? !!savedItem.isrequired
                  : !!masterItem.isrequired,
              displayorder: savedItem.displayorder || masterItem.displayorder,
              assigned: true,
            };
          }
          return {
            ...masterItem,
            studychecklistid: null,
            assigned: false,
          };
        });
        const customItems = savedContext?.customItems || [];
        items = [...mappedMasters, ...customItems];
      } else {
        items = masterItems.map((m: any) => ({ ...m, assigned: true }));
      }
    }

    items.forEach((item: any) => {
      const trackingId = item.id || item.masterchecklistid || item.studychecklistid;

      let isAssigned = item.assigned;
      if (currentMap[trackingId] !== undefined) {
        isAssigned = currentMap[trackingId];
      } else if (isEditMode) {
        if (item.isCustom) {
          isAssigned = true;
        } else {
          const masterId = Number(item.masterchecklistid || item.id);
          isAssigned = savedContext?.masterIds.has(masterId) ?? false;
        }
      }

      if (isAssigned) {
        const typeId = Number(item.checklisttype || typeVal);
        const rawCat = item.checklistcategory || categoryMap.get(typeId);
        const catVal = rawCat ? Number(rawCat) : null;

        const rawOrder =
          item.displayorder !== undefined && item.displayorder !== null
            ? item.displayorder
            : globalPreOrder++;
        const orderPadded = String(rawOrder).padStart(2, "0");

        beforeenrollment.push({
          studychecklistid: item.studychecklistid || null,
          masterchecklistid: item.isCustom ? null : item.masterchecklistid || null,
          question: item.question,
          checklisttype: typeId,
          checklistcategory: catVal,
          isrequired: !!item.isrequired,
          displayorder: orderPadded,
        });
      }
    });
  });

  // 2. Process Period-Based Checklists (afterenrollment)
  const periodCount = Math.max(1, numPeriods);

  for (let p = 1; p <= periodCount; p++) {
    const hiddenList = hiddenTypeValuesState[`p${p}`] || [];
    const hiddenSet = new Set(hiddenList.map(Number));

    const periodChecklists: any[] = [];
    let globalIndex = 1;

    allTypeIds.forEach((typeVal) => {
      if (isPreEnrollmentType(typeVal) || hiddenSet.has(typeVal)) return;

      const key = `p${p}_${typeVal}`;
      const currentMap = assignedState[key] || {};
      const savedContext = savedContextMap[key];

      let items: any[] = [];
      if (customChecklists[key]) {
        items = customChecklists[key];
      } else if (Array.isArray(rawApiData)) {
        const masterItems = rawApiData.filter(
          (item: any) => Number(item.checklisttype) === typeVal
        );
        if (isEditMode) {
          const mappedMasters = masterItems.map((masterItem: any) => {
            const masterId = Number(masterItem.masterchecklistid || masterItem.id);
            const savedItem = savedContext?.savedMasterMap.get(masterId);
            if (savedItem) {
              return {
                ...masterItem,
                studychecklistid: Number(savedItem.studychecklistid) || null,
                question: savedItem.question || masterItem.question,
                isrequired:
                  savedItem.isrequired !== undefined
                    ? !!savedItem.isrequired
                    : !!masterItem.isrequired,
                displayorder: savedItem.displayorder || masterItem.displayorder,
                assigned: true,
              };
            }
            return {
              ...masterItem,
              studychecklistid: null,
              assigned: false,
            };
          });
          const customItems = savedContext?.customItems || [];
          items = [...mappedMasters, ...customItems];
        } else {
          items = masterItems.map((m: any) => ({ ...m, assigned: true }));
        }
      }

      items.forEach((item: any) => {
        const trackingId = item.id || item.masterchecklistid || item.studychecklistid;

        let isAssigned = item.assigned;
        if (currentMap[trackingId] !== undefined) {
          isAssigned = currentMap[trackingId];
        } else if (isEditMode) {
          if (item.isCustom) {
            isAssigned = true;
          } else {
            const masterId = Number(item.masterchecklistid || item.id);
            isAssigned = savedContext?.masterIds.has(masterId) ?? false;
          }
        }

        if (isAssigned) {
          const typeId = Number(item.checklisttype || typeVal);
          const rawCat = item.checklistcategory || categoryMap.get(typeId);
          const catVal = rawCat ? Number(rawCat) : null;

          const rawOrder =
            item.displayorder !== undefined && item.displayorder !== null
              ? item.displayorder
              : globalIndex++;
          const orderPadded = String(rawOrder).padStart(2, "0");

          periodChecklists.push({
            studychecklistid: item.studychecklistid || null,
            masterchecklistid: item.isCustom ? null : item.masterchecklistid || null,
            question: item.question,
            checklisttype: typeId,
            checklistcategory: catVal,
            isrequired: !!item.isrequired,
            displayorder: orderPadded,
          });
        }
      });
    });

    afterenrollment.push({
      periodid: p,
      checklists: periodChecklists,
    });
  }

  return {
    studychecklists: {
      beforeenrollment,
      afterenrollment,
    },
  };
};

export const viewchecklistscolumns = [
  {
    key: "question",
    label: "Checklist Question",
    render: (val: string, row: any) => (
      <span className="fw-medium text-dark">
        {val || row.question}
        {row?.isrequired && <span className="text-danger ms-1">*</span>}
      </span>
    ),
  },
  {
    key: "isrequired",
    label: "Is Required",
    textCenter: true,
    width: "120px",
    render: (val: boolean) => (
      <span
        className={`badge ${val ? "bg-danger-subtle text-danger" : "bg-secondary-subtle text-secondary"
          } px-2 py-1 fs-12`}
      >
        {val ? "Yes" : "No"}
      </span>
    ),
  },
];


export const checkliststepcolumns = (isAllAssigned: boolean, currentChecklists: any[], handleToggleSelectAll: () => void, handleToggleAssign: (id: number) => void): Column<any>[] => [
  {
    key: "assigned",
    label: (
      <div className="d-flex align-items-center justify-content-center w-100">
        <input
          type="checkbox"
          checked={isAllAssigned}
          disabled={currentChecklists.length === 0}
          style={{ cursor: "pointer" }}
          onChange={handleToggleSelectAll}
          title={isAllAssigned ? "Deselect All" : "Select All"}
        />
      </div>
    ) as any,
    textCenter: true,
    width: "60px",
    render: (_: any, row: any) => (
      <div className="d-flex align-items-center justify-content-center w-100">
        <input
          type="checkbox"
          checked={!!row?.assigned}
          style={{ cursor: "pointer" }}
          onChange={() => handleToggleAssign(row.id || row.masterchecklistid)}
        />
      </div>
    ),
  },
  {
    key: "question",
    label: "Checklist",
    render: (val: string, row: any) => (
      <span className="fw-medium text-dark">
        <TruncatedText text={val || row.question} maxWidth="400px" />
        {row?.isrequired && <span className="text-danger ms-1">*</span>}
      </span>
    ),
  },
  {
    key: "isrequired",
    label: "Is Required",
    textCenter: true,
    width: "120px",
    render: (val: boolean) => (
      <span
        className={`badge ${val ? "bg-danger-subtle text-danger" : "bg-secondary-subtle text-secondary"
          } px-2 py-1 fs-12`}
      >
        {val ? "Yes" : "No"}
      </span>
    ),
  },
];

export const crfstepcolumns = (isAllAssigned: boolean, currentCRFs: any[], handleToggleSelectAll: () => void, handleToggleAssign: (id: number) => void): Column<any>[] => [
  {
    key: "assigned",
    label: (
      <div className="d-flex align-items-center justify-content-center w-100">
        <input
          type="checkbox"
          checked={isAllAssigned}
          disabled={currentCRFs.length === 0}
          style={{ cursor: "pointer" }}
          onChange={handleToggleSelectAll}
          title={isAllAssigned ? "Deselect All" : "Select All"}
        />
      </div>
    ) as any,
    textCenter: true,
    width: "60px",
    render: (_: any, row: any) => (
      <div className="d-flex align-items-center justify-content-center w-100">
        <input
          type="checkbox"
          checked={!!row?.assigned}
          style={{ cursor: "pointer" }}
          onChange={() => handleToggleAssign(row.id || row.crfid)}
        />
      </div>
    ),
  },
  {
    key: "name",
    label: "CRF Name",
    render: (val: string, row: any) => (
      <span className="fw-medium text-dark">
        <TruncatedText text={val || row.name || row.crfname || row.crfName || `CRF #${row.crfid}`} maxWidth="400px" />
      </span>
    ),
  },
];

export const buildStudyCRFsPayload = (formValues: any, rawApiData: any[] = []) => {
  const numPeriods = Number(formValues?.numberofperiods) || 1;
  const config = formValues?.configuredCRFsData || {};
  const {
    assignedState = {},
    customCRFs = {},
    hiddenCategoriesState = {},
    isEditMode = false,
    existingStudyCRFs = null,
    allCRFsData = {},
    activeCategories = [],
  } = config;

  // Create a mapping from actual database periodid to the 1-based period sort index (1, 2, 3...)
  const periodIdToNoMap = new Map<number, number>();
  if (Array.isArray(formValues?.periods)) {
    formValues.periods.forEach((period: any, index: number) => {
      const pId = Number(period.periodid || period.period_id || period.id);
      if (pId) {
        periodIdToNoMap.set(pId, index + 1);
      }
    });
  }

  const studycrfs: any[] = [];
  const periodCount = Math.max(1, numPeriods);

  const hasAllCRFs = Object.keys(allCRFsData).length > 0;

  for (let p = 1; p <= periodCount; p++) {
    const periodCRFsList: any[] = [];
    let globalIndex = 1;

    if (hasAllCRFs) {
      Object.keys(allCRFsData).forEach((modIdStr) => {
        const moduleId = Number(modIdStr);
        const hiddenList = hiddenCategoriesState[`p${p}`] || [];
        const hiddenSet = new Set(hiddenList.map(Number));
        if (hiddenSet.has(moduleId)) return;

        const key = `p${p}_${moduleId}`;
        const crfMap = assignedState[key] || {};
        const masterItems = allCRFsData[moduleId] || [];

        masterItems.forEach((masterItem: any) => {
          const crfId = Number(masterItem.crfid || masterItem.crfId || masterItem.id);

          let isAssigned = false;
          if (crfMap[crfId] !== undefined) {
            isAssigned = crfMap[crfId];
          } else if (isEditMode) {
            isAssigned = Array.isArray(existingStudyCRFs) && existingStudyCRFs.some((saved: any) => {
              const savedPeriodId = Number(saved.periodid);
              const periodNoFromId = savedPeriodId ? periodIdToNoMap.get(savedPeriodId) : undefined;
              const savedPeriod = periodNoFromId || Number(saved.period_sort || saved.period_no) || 1;
              const savedCrfId = Number(saved.crfid || saved.id);
              const savedModuleId = Number(saved.crfmodule || saved.moduleid || saved.crfcategory);
              return savedPeriod === p && savedCrfId === crfId && savedModuleId === moduleId;
            });
          } else {
            isAssigned = false;
          }

          if (isAssigned) {
            const customList = customCRFs[key] || [];
            const item = customList.find((c: any) => Number(c.crfid || c.crfId || c.id) === crfId);

            const savedItem = (isEditMode && Array.isArray(existingStudyCRFs))
              ? existingStudyCRFs.find((s: any) => {
                const savedPeriodId = Number(s.periodid);
                const periodNoFromId = savedPeriodId ? periodIdToNoMap.get(savedPeriodId) : undefined;
                const savedPeriod = periodNoFromId || Number(s.period_sort || s.period_no) || 1;
                const savedCrfId = Number(s.crfid || s.id);
                const savedModuleId = Number(s.crfmodule || s.moduleid || s.crfcategory);
                return savedPeriod === p && savedCrfId === crfId && savedModuleId === moduleId;
              })
              : null;

            let studycrfidVal = item?.studycrfid || null;
            if (!studycrfidVal && savedItem) {
              studycrfidVal = Number(savedItem.studycrfid) || null;
            }

            const rawOrder = item?.displayorder !== undefined && item?.displayorder !== null
              ? item.displayorder
              : (savedItem?.displayorder !== undefined && savedItem?.displayorder !== null
                ? savedItem.displayorder
                : globalIndex++);
            const orderPadded = String(rawOrder).padStart(2, "0");

            const categoryMeta = activeCategories.find((c: any) => Number(c.value) === moduleId);
            const parentCrfCategory = categoryMeta && categoryMeta.crfcategory ? Number(categoryMeta.crfcategory) : moduleId;

            periodCRFsList.push({
              studycrfid: studycrfidVal,
              crfid: crfId,
              crfmodule: moduleId,
              crfcategory: parentCrfCategory,
              displayorder: orderPadded,
            });
          }
        });
      });
    } else {
      Object.keys(assignedState).forEach((key) => {
        const parts = key.split("_");
        if (parts.length !== 2) return;

        const periodNum = Number(parts[0].replace("p", ""));
        const moduleId = Number(parts[1]);

        if (periodNum !== p) return;

        const hiddenList = hiddenCategoriesState[`p${p}`] || [];
        const hiddenSet = new Set(hiddenList.map(Number));
        if (hiddenSet.has(moduleId)) return;

        const crfMap = assignedState[key] || {};
        Object.keys(crfMap).forEach((crfIdStr) => {
          const crfId = Number(crfIdStr);
          const isAssigned = crfMap[crfId];

          if (isAssigned) {
            const customList = customCRFs[key] || [];
            const item = customList.find((c: any) => Number(c.crfid || c.crfId || c.id) === crfId);

            const savedItem = (isEditMode && Array.isArray(existingStudyCRFs))
              ? existingStudyCRFs.find((s: any) => {
                const savedPeriodId = Number(s.periodid);
                const periodNoFromId = savedPeriodId ? periodIdToNoMap.get(savedPeriodId) : undefined;
                const savedPeriod = periodNoFromId || Number(s.period_sort || s.period_no) || 1;
                const savedCrfId = Number(s.crfid || s.id);
                const savedModuleId = Number(s.crfmodule || s.moduleid || s.crfcategory);
                return savedPeriod === p && savedCrfId === crfId && savedModuleId === moduleId;
              })
              : null;

            let studycrfidVal = item?.studycrfid || null;
            if (!studycrfidVal && savedItem) {
              studycrfidVal = Number(savedItem.studycrfid) || null;
            }

            const rawOrder = item?.displayorder !== undefined && item?.displayorder !== null
              ? item.displayorder
              : (savedItem?.displayorder !== undefined && savedItem?.displayorder !== null
                ? savedItem.displayorder
                : globalIndex++);
            const orderPadded = String(rawOrder).padStart(2, "0");

            const categoryMeta = activeCategories.find((c: any) => Number(c.value) === moduleId);
            const parentCrfCategory = categoryMeta && categoryMeta.crfcategory ? Number(categoryMeta.crfcategory) : moduleId;

            periodCRFsList.push({
              studycrfid: studycrfidVal,
              crfid: crfId,
              crfmodule: moduleId,
              crfcategory: parentCrfCategory,
              displayorder: orderPadded,
            });
          }
        });
      });
    }

    studycrfs.push({
      periodid: p,
      crfs: periodCRFsList,
    });
  }

  return {
    studycrfs,
  };
};